import React, { useEffect, useState, useMemo, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import LoadingButton from '@mui/lab/LoadingButton';
import { ToggleButtonGroup, ToggleButton } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { useMediaDevices } from 'react-media-devices';
import { useZxing } from 'react-zxing';
import { analyzeBarCode } from '../../../../api';
import { MealType } from '../../../../common/commonTypes';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';
import colors from 'tailwindcss/colors';

const constraints: MediaStreamConstraints = {
    video: true,
    audio: false,
};

type BarCodeScanModalType = {
    onCancel: () => void;
    onSuccess: (mealToSave: MealType) => void;
};

const BarCodeScanModal = (props: BarCodeScanModalType) => {
    const { onCancel, onSuccess } = props;

    const { t, i18n } = useTranslation();

    const [deviceId, setDeviceId] = useState('');
    const [loading, setLoading] = useState(false);
    const [alertMessage, setAlertMessage] = useState<{
        type: 'warning' | 'error';
        text: string;
    } | null>(null);

    // Retrieve all devices
    const { devices } = useMediaDevices({ constraints });

    // Memoize the filtering of video devices
    const availVideoDevices = useMemo(() => {
        const seenGroups = new Set();
        return (devices ?? []).filter((device) => {
            if (
                device.kind !== 'videoinput' ||
                !device.deviceId ||
                !device.groupId ||
                device.label.toLowerCase().includes('front') ||
                device.label.toLowerCase().includes('wide') ||
                device.label.toLowerCase().includes('telephoto') ||
                device.label.toLowerCase().includes('dual') ||
                device.label.toLowerCase().includes('triple') ||
                device.label.toLowerCase().includes('camera 2, facing back')
            ) {
                return false;
            }
            // If we've already seen this groupId, skip this device.
            if (seenGroups.has(device.groupId)) {
                return false;
            }
            // Mark this groupId as seen and include this device.
            seenGroups.add(device.groupId);
            return true;
        });
    }, [devices]);

    // console.log('%c availVideoDevices >>> ', 'background: #333; color: #bada55', availVideoDevices);
    // alert(JSON.stringify(availVideoDevices));

    const handleNotFound = () => {
        setLoading(false);
        // @ts-ignore
        setAlertMessage({ type: 'warning', text: t('product_not_found') });
        setTimeout(() => {
            setAlertMessage(null);
        }, 3000);
    };

    const handleBEErr = (error: any) => {
        setLoading(false);
        // @ts-ignore
        setAlertMessage({ type: 'error', text: "Can't analyze the barcode." });
        setTimeout(() => {
            setAlertMessage(null);
        }, 3000);
    };

    const getMealData = async (barcode: string) => {
        setLoading(true);

        try {
            const res = await analyzeBarCode(barcode);

            if (!res.data) {
                handleNotFound();
                return;
            }

            const mealToSave: MealType = {
                ...res.data,
                name: res.data.name === 'UNKNOWN' ? t('unknown') : res.data.name,
            };

            onSuccess(mealToSave);
        } catch (error: any) {
            if (error.status === 404) {
                handleNotFound();
            } else {
                handleBEErr(error);
                console.error('Error analyzing barcode:', error);
                setLoading(false);
            }
        }
    };

    // Set the last available device if not set yet
    useEffect(() => {
        if (!deviceId && availVideoDevices.length > 0) {
            const lastDevice = availVideoDevices[availVideoDevices.length - 1];
            setDeviceId(lastDevice.deviceId);
        }
    }, [deviceId, availVideoDevices]);

    // Use a stable callback for decode results to avoid re-initialization
    const handleDecodeResult = useCallback((decoded: any) => {
        const barcode: string = decoded.getText();
        getMealData(barcode);
    }, []);

    // Initialize scanner
    const { ref } = useZxing({
        deviceId, // pass the selected device ID
        paused: !deviceId, // only scan if there's a valid device
        onDecodeResult: handleDecodeResult,
    });

    const handleCancel = () => {
        onCancel();
    };

    return (
        <Dialog
            open
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    handleCancel();
                }
            }}
        >
            <img src="/calPal_fork_transp_s_cut.png" className="absolute bottom-0 left-0" />

            <DialogTitle>{t('scan_barcode')}</DialogTitle>
            <DialogContent>
                <div className="relative">
                    {alertMessage && (
                        <Alert severity={alertMessage?.type} className="mb-2">
                            {alertMessage?.text}
                        </Alert>
                    )}

                    <video ref={ref} style={{ width: '100%', background: colors.gray[100] }} />

                    <div className="text-center text-xs text-gray-700 my-2">
                        {t('scanning_depends')}
                    </div>

                    {availVideoDevices.length > 1 && (
                        <ToggleButtonGroup
                            size="small"
                            color="primary"
                            value={deviceId}
                            exclusive
                            onChange={(e, newId) => setDeviceId(newId)}
                            orientation="vertical"
                        >
                            {availVideoDevices.map((device, idx) => (
                                <ToggleButton key={device.deviceId} value={device.deviceId}>
                                    {/*<PhotoCameraIcon /> Camera {idx + 1}*/}
                                    <PhotoCameraIcon />
                                    {device.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    )}

                    {loading && (
                        <div className="absolute top-0 bottom-0 left-0 right-0 bg-neutral-100 rounded flex flex-col items-center justify-center">
                            <div className="mb-2 text-center text-gray-400">
                                <CircularProgress size={30} sx={{ color: 'inherit' }} />
                            </div>
                            <div className="text-center text-xs text-gray-500">
                                {t('analyzing')}
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
            <DialogActions>
                <LoadingButton onClick={handleCancel}>{t('MODAL_cancel')}</LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default BarCodeScanModal;
